import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCorners,
  DragOverlay,
} from "@dnd-kit/core";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { List, LayoutGrid, AlertTriangle, ChevronUp, Minus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { fmtRelative, RETURN_TYPE_LABELS } from "@/lib/format";
import { useRealtimeEvent } from "@/lib/realtime-context";

const PRIO_ICON = { urgent: AlertTriangle, high: ChevronUp, medium: Minus, low: ChevronDown };
const PRIO_COLOR = { urgent: "text-red-500", high: "text-amber-500", medium: "text-muted-foreground", low: "text-muted-foreground/60" };

function KanbanCard({ ret }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: ret.id });
  const PrioIcon = PRIO_ICON[ret.priority] || Minus;
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1 } : {};

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="bg-card border border-border rounded-lg p-3 shadow-sm hover:shadow-md hover:border-foreground/20 transition-all cursor-grab active:cursor-grabbing"
      data-testid={`kanban-card-${ret.return_number}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="font-mono text-[10px] text-muted-foreground">{ret.return_number}</div>
        <PrioIcon className={`h-3.5 w-3.5 ${PRIO_COLOR[ret.priority]}`} />
      </div>
      <div className="text-sm font-medium truncate mb-1">{ret.customer_name}</div>
      <div className="text-xs text-muted-foreground">#{ret.order_number} · {ret.items?.length || 0} produse</div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{RETURN_TYPE_LABELS[ret.return_type]}</Badge>
        <span className="text-[10px] text-muted-foreground">{fmtRelative(ret.created_at)}</span>
      </div>
    </div>
  );
}

function KanbanColumn({ status, items }) {
  const { setNodeRef, isOver } = useDroppable({ id: status.id });
  return (
    <div className="w-80 shrink-0" data-testid={`kanban-col-${status.name}`}>
      <div className="flex items-center justify-between px-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: status.color }} />
          <span className="font-semibold text-sm">{status.name}</span>
          <span className="text-xs text-muted-foreground font-mono">{items.length}</span>
        </div>
      </div>
      <div
        ref={setNodeRef}
        className={`bg-muted/30 rounded-lg p-2 min-h-[400px] space-y-2 transition-colors ${isOver ? "bg-primary/10 ring-2 ring-primary/30" : ""}`}
      >
        {items.length === 0 && <div className="text-xs text-muted-foreground/60 text-center py-8">Goală</div>}
        {items.map((it) => (
          <Link key={it.id} to={`/retururi/${it.id}`} onClick={(e) => e.target.closest('[draggable]') && e.preventDefault()}>
            <KanbanCard ret={it} />
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function ReturnsKanbanPage() {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get("/returns"), api.get("/returns/statuses")]);
      setReturns(r.data);
      setStatuses(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const debouncedReload = useCallback(() => {
    if (debouncedReload._t) clearTimeout(debouncedReload._t);
    debouncedReload._t = setTimeout(() => { load(); }, 400);
  }, []);
  useRealtimeEvent("return.created", debouncedReload);
  useRealtimeEvent("return.updated", debouncedReload);

  const byStatus = useMemo(() => {
    const out = {};
    statuses.forEach((s) => (out[s.id] = []));
    returns.forEach((r) => {
      if (!out[r.status_id]) out[r.status_id] = [];
      out[r.status_id].push(r);
    });
    return out;
  }, [returns, statuses]);

  const activeReturn = returns.find((r) => r.id === activeId);

  const onDragEnd = async (e) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ret = returns.find((r) => r.id === active.id);
    if (!ret || ret.status_id === over.id) return;
    // optimistic
    const prev = ret.status_id;
    setReturns((cur) => cur.map((r) => (r.id === active.id ? { ...r, status_id: over.id } : r)));
    try {
      await api.patch(`/returns/${active.id}`, { status_id: over.id });
      const newStatus = statuses.find((s) => s.id === over.id);
      toast.success(`Mutat în "${newStatus?.name}"`);
    } catch (err) {
      setReturns((cur) => cur.map((r) => (r.id === active.id ? { ...r, status_id: prev } : r)));
      toast.error("Nu am putut schimba statusul");
    }
  };

  return (
    <div className="space-y-6" data-testid="returns-kanban-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Modul · Retururi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Kanban</h1>
          <p className="text-muted-foreground text-sm mt-1">Trage și mută cardurile între statusuri.</p>
        </div>
        <Tabs value="kanban">
          <TabsList>
            <TabsTrigger value="list" onClick={() => navigate("/retururi")}><List className="h-4 w-4 mr-1.5" /> Listă</TabsTrigger>
            <TabsTrigger value="kanban"><LayoutGrid className="h-4 w-4 mr-1.5" /> Kanban</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="w-80 h-[420px] shrink-0" />
          ))}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={(e) => setActiveId(e.active.id)}
          onDragEnd={onDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            {statuses.map((s) => (
              <KanbanColumn key={s.id} status={s} items={byStatus[s.id] || []} />
            ))}
          </div>
          <DragOverlay>
            {activeReturn ? <KanbanCard ret={activeReturn} /> : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}
